import React, { useState } from 'react';
import {
    Box,
    Button,
    Chip,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import TurtleMascot from './TurtleMascot';
import {
    LANGUAGES,
    LANGUAGE_NAMES,
    createEmptyTranslations,
} from '../constants/languages';

const AddTermForm = ({ onTermAdded }) => {
    const [productName, setProductName] = useState('');
    const [translations, setTranslations] = useState(createEmptyTranslations);
    const [visibleLanguages, setVisibleLanguages] = useState(['en']);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const handleInputChange = (language, value) => {
        setTranslations((prevTranslations) => ({
            ...prevTranslations,
            [language]: value,
        }));
    };

    const handleLanguageChange = (event) => {
        const value = event.target.value;
        const nextLanguages = typeof value === 'string' ? value.split(',') : value;
        setVisibleLanguages(nextLanguages.length ? nextLanguages : ['en']);
    };

    const handleSubmit = async () => {
        const trimmedProductName = productName.trim();

        if (!trimmedProductName) {
            setErrorMessage('Please enter a product name.');
            setTimeout(() => setErrorMessage(''), 3000);
            return;
        }

        const newTerm = {
            productName: trimmedProductName,
            translations: Object.fromEntries(
                Object.entries(translations).map(([language, value]) => [
                    language,
                    value.trim(),
                ])
            ),
        };

        try {
            await onTermAdded(newTerm);
            setSuccessMessage('Term added successfully!');
            setTimeout(() => setSuccessMessage(''), 3000);

            setProductName('');
            setTranslations(createEmptyTranslations());
            setVisibleLanguages(['en']);
        } catch (error) {
            setErrorMessage('Error adding term. Please try again.');
            console.error('Error adding term:', error);
            setTimeout(() => setErrorMessage(''), 3000);
        }
    };

    return (
        <Box>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                Add term
            </Typography>

            {successMessage && <TurtleMascot message={successMessage} type="success" />}
            {errorMessage && <TurtleMascot message={errorMessage} type="error" />}

            <Box sx={{ display: 'grid', gap: 2 }}>
                <TextField
                    fullWidth
                    size="small"
                    label="Product name"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    placeholder="Enter the product name"
                />

                <TextField
                    select
                    fullWidth
                    size="small"
                    label="Languages"
                    value={visibleLanguages}
                    onChange={handleLanguageChange}
                    SelectProps={{
                        multiple: true,
                        renderValue: (selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((code) => (
                                    <Chip
                                        key={code}
                                        size="small"
                                        label={LANGUAGE_NAMES[code] || code.toUpperCase()}
                                    />
                                ))}
                            </Box>
                        ),
                    }}
                >
                    {LANGUAGES.map(({ code, name }) => (
                        <MenuItem key={code} value={code}>
                            {name}
                        </MenuItem>
                    ))}
                </TextField>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                        gap: 1.5,
                    }}
                >
                    {visibleLanguages.map((language) => (
                        <TextField
                            key={language}
                            fullWidth
                            size="small"
                            label={LANGUAGE_NAMES[language]}
                            value={translations[language] || ''}
                            onChange={(e) => handleInputChange(language, e.target.value)}
                            placeholder={`Enter ${LANGUAGE_NAMES[language].toLowerCase()} translation`}
                        />
                    ))}
                </Box>
            </Box>

            <Button variant="contained" onClick={handleSubmit} sx={{ mt: 2 }}>
                Add term
            </Button>
        </Box>
    );
};

export default AddTermForm;
